/**
 * Open Items Inquiry — unified view of all outstanding AR/AP documents.
 * Supports filtering by party type, party, currency, status, date range.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  Search, RefreshCw, Filter, MapPin, Layers, FileText, Download,
} from 'lucide-react';

import { openItemApi }   from '@/api/openItem.api';
import { exportToCsv }   from '@/utils/exportCsv';
import { usePermission } from '@/hooks/usePermission';
import { Permissions }   from '@/constants/permissions';
import { Spinner }       from '@/components/ui/Spinner';
import { DocumentReferences } from '@/components/fin/DocumentReferences';
import { cn }            from '@/utils/cn';
import type { RootState } from '@/state/store';
import { isCreditItem, type OpenItemDto, type OpenItemFilterDto, type OpenItemStatus } from '@/types/openItem.types';

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });

const STATUS_COLORS: Record<OpenItemStatus, string> = {
  Open:               'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  PartiallyAllocated: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
  FullyAllocated:     'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
  Overpaid:           'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
  Reversed:           'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-gray-400',
  Cancelled:          'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
  WrittenOff:         'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
};

export const OpenItemsPage = () => {
  const { hasPermission } = usePermission();
  const canView = hasPermission(Permissions.FIN_OPEN_ITEM_VIEW);
  const canViewAllLoc = hasPermission(Permissions.ALLOCATION_VIEW_ALL_LOCATIONS) || hasPermission(Permissions.TXN_VIEW_ALL_LOCATIONS);
  const canViewAllCC  = hasPermission(Permissions.ALLOCATION_VIEW_ALL_COST_CENTERS) || hasPermission(Permissions.TXN_VIEW_ALL_COST_CENTERS);

  const session      = useSelector((s: RootState) => s.session.session);
  const [qp]         = useSearchParams();

  // Read URL query params for drilldown from outstanding pages
  const qpPartyType  = qp.get('partyType') as 'Customer' | 'Supplier' | null;
  const qpPartyId    = qp.get('partyId')   ?? undefined;

  const [items,   setItems]   = useState<OpenItemDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const pageSize = 25;

  const [filter, setFilter] = useState<OpenItemFilterDto>({
    pageNumber:     1,
    pageSize,
    sortDescending: true,
    partyType:     qpPartyType   ?? undefined,
    partyId:       qpPartyId     ?? undefined,
    locationId:    session?.locationId   || undefined,
    costCenterId:  session?.costCenterId || undefined,
  });

  const [partyTypeFilter, setPartyTypeFilter] = useState<'All' | 'Customer' | 'Supplier'>(
    qpPartyType ?? 'All'
  );
  const [statusFilter,    setStatusFilter]    = useState<OpenItemStatus | ''>('');
  const [searchDoc,       setSearchDoc]       = useState('');
  const [overdueOnly,     setOverdueOnly]     = useState(false);

  const prevSession = useRef(session);
  useEffect(() => {
    const prev = prevSession.current;
    prevSession.current = session;
    if (!session) return;
    if (session.locationId !== prev?.locationId || session.costCenterId !== prev?.costCenterId) {
      setFilter(f => ({ ...f, pageNumber: 1, locationId: session.locationId || undefined, costCenterId: session.costCenterId || undefined }));
      setPage(1);
    }
  }, [session]);

  const load = useCallback(async (f: OpenItemFilterDto) => {
    if (!canView) return;
    setLoading(true);
    try {
      const res  = await openItemApi.getList(f);
      const data = res.data?.data;
      setItems(data?.items ?? []);
      setTotal(data?.totalCount ?? 0);
    } catch (e) { console.error('Failed to load open items', e); }
    finally { setLoading(false); }
  }, [canView]);

  useEffect(() => { load(filter); }, [filter, load]);

  const applyFilters = () => {
    const newFilter: OpenItemFilterDto = {
      ...filter,
      partyType:      partyTypeFilter === 'All' ? undefined : partyTypeFilter,
      status:         statusFilter || undefined,
      documentNumber: searchDoc || undefined,
      overdueOnly:    overdueOnly || undefined,
      pageNumber:     1,
    };
    setFilter(newFilter);
    setPage(1);
  };

  const handlePageChange = (p: number) => { setPage(p); setFilter(f => ({ ...f, pageNumber: p })); };

  const totalPages = Math.ceil(total / pageSize);

  if (!canView) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400">
        <p className="text-sm">You do not have permission to view open items.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">

      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white">Open Items Inquiry</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{total.toLocaleString()} item{total !== 1 ? 's' : ''}</p>
          </div>
          <div className="flex items-center gap-2">
            {items.length > 0 && (
              <button
                onClick={() => exportToCsv('open-items.csv', items.map(i => ({
                  PartyType: i.partyType, PartyCode: i.partyCode, PartyName: i.partyName,
                  DocumentType: i.documentType, DocumentNumber: i.documentNumber,
                  // An exported list is usually being reconciled against a supplier's statement,
                  // so their reference matters more than ours.
                  TheirReference: i.externalDocumentNumber ?? '',
                  SourceDocument: i.sourceDocumentNumber ?? '',
                  Origin: i.origin ?? '',
                  DocumentDate: i.documentDate, DueDate: i.dueDate ?? '',
                  Currency: i.currencyCode, Original: i.originalAmount,
                  Allocated: i.allocatedAmount, Outstanding: i.outstandingAmount,
                  BaseOriginal: i.baseOriginalAmount, BaseAllocated: i.baseAllocatedAmount,
                  BaseOutstanding: i.baseOutstandingAmount, Status: i.status,
                  AgeingBucket: i.ageingBucket, DaysOverdue: i.daysOverdue ?? '',
                })))}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-600 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-300 rounded-lg transition-colors">
                <Download size={13} /> Export
              </button>
            )}
            <button onClick={() => load(filter)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors" title="Refresh">
              <RefreshCw size={16} />
            </button>
          </div>
        </div>

        {/* Scope chips */}
        {(session?.locationName || session?.costCenterName) && (
          <div className="flex items-center gap-2 mt-3">
            <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wider flex items-center gap-1"><MapPin size={10} /> Scope:</span>
            {session?.locationName && (
              <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium', !canViewAllLoc ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 ring-1 ring-blue-200' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400 ring-1 ring-slate-200')}>
                <MapPin size={10} />{session.locationCode} — {session.locationName}
              </span>
            )}
            {session?.costCenterName && (
              <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium', !canViewAllCC ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 ring-1 ring-blue-200' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400 ring-1 ring-slate-200')}>
                <Layers size={10} />{session.costCenterCode} — {session.costCenterName}
              </span>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3 mt-4 flex-wrap">
          {/* Party type */}
          <div className="flex items-center rounded-lg overflow-hidden ring-1 ring-slate-200 dark:ring-slate-600">
            {(['All', 'Customer', 'Supplier'] as const).map(pt => (
              <button key={pt} onClick={() => setPartyTypeFilter(pt)}
                className={cn('px-3 py-1.5 text-xs font-medium transition-colors', partyTypeFilter === pt ? 'bg-blue-600 text-white' : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/60')}>
                {pt}
              </button>
            ))}
          </div>

          {/* Status filter */}
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as OpenItemStatus | '')}
            className="text-sm border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-1.5 bg-white dark:bg-slate-700 text-slate-700 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none">
            <option value="">All statuses</option>
            <option value="Open">Open</option>
            <option value="PartiallyAllocated">Partially Allocated</option>
            <option value="FullyAllocated">Fully Allocated</option>
            <option value="Overpaid">Overpaid</option>
            <option value="Reversed">Reversed</option>
          </select>

          {/* Document search */}
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={searchDoc} onChange={e => setSearchDoc(e.target.value)} onKeyDown={e => e.key === 'Enter' && applyFilters()} placeholder="Document number…"
              className="pl-9 pr-3 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none w-44" />
          </div>

          {/* Overdue toggle */}
          <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 cursor-pointer">
            <input type="checkbox" checked={overdueOnly} onChange={e => setOverdueOnly(e.target.checked)} className="accent-blue-600" />
            Overdue only
          </label>

          <button onClick={applyFilters} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors">
            <Filter size={12} /> Apply
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {loading ? (
          <div className="flex items-center justify-center h-48"><Spinner size="md" /></div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-slate-400">
            <FileText size={40} className="mb-3 opacity-30" />
            <p className="text-sm font-medium">No open items found</p>
          </div>
        ) : (
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm ring-1 ring-slate-200 dark:ring-slate-700 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-700/60 border-b border-slate-200 dark:border-slate-700 text-xs text-slate-500 dark:text-gray-400 uppercase tracking-wider">
                  {['Party', 'Document', 'Date', 'Due Date', 'Original', 'Outstanding', 'Currency', 'Status', 'Bucket'].map(h => (
                    <th key={h} className="px-4 py-3 text-left font-semibold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {items.map(item => {
                  // Credits (debit notes, credit notes, payments, advances) reduce the balance.
                  const sign = isCreditItem(item) ? -1 : 1;
                  return (
                  <tr key={item.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900 dark:text-white text-xs">{item.partyName}</div>
                      <div className="text-[11px] text-slate-400">{item.partyCode} · {item.partyType}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-mono text-xs text-blue-700 dark:text-blue-400">{item.documentNumber}</div>
                      <div className="text-[10px] text-slate-400">{item.documentType.replace(/([A-Z])/g, ' $1').trim()}</div>
                      {/* Outstanding is where a supplier query starts ("what about our INV-…?"),
                          so show their reference and the source doc, not just our number. */}
                      <DocumentReferences item={item} showSourceNumber />
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600 dark:text-slate-400 whitespace-nowrap">{fmtDate(item.documentDate)}</td>
                    <td className="px-4 py-3 text-xs text-slate-600 dark:text-slate-400 whitespace-nowrap">
                      {item.dueDate ? fmtDate(item.dueDate) : '—'}
                      {item.daysOverdue && item.daysOverdue > 0
                        ? <span className="ml-1 text-red-500 text-[10px]">({item.daysOverdue}d)</span>
                        : null}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs text-slate-700 dark:text-slate-300 whitespace-nowrap">{fmt(sign * item.originalAmount)}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs font-semibold whitespace-nowrap">
                      <span className={cn(item.outstandingAmount <= 0 ? 'text-slate-400' : sign < 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400')}>
                        {fmt(sign * item.outstandingAmount)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-slate-500 dark:text-gray-400">{item.currencyCode}</td>
                    <td className="px-4 py-3">
                      <span className={cn('text-[11px] font-medium px-2 py-0.5 rounded-full', STATUS_COLORS[item.status])}>{item.status.replace(/([A-Z])/g, ' $1').trim()}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500 dark:text-gray-400">{item.ageingBucket}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <span className="text-xs text-slate-500 dark:text-gray-400">Page {page} of {totalPages} · {total.toLocaleString()} records</span>
            <div className="flex items-center gap-1">
              <button onClick={() => handlePageChange(page - 1)} disabled={page <= 1} className="p-1.5 rounded-lg text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed">‹</button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                return <button key={p} onClick={() => handlePageChange(p)} className={cn('w-7 h-7 text-xs rounded-lg', p === page ? 'bg-blue-600 text-white font-medium' : 'text-slate-600 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-slate-700')}>{p}</button>;
              })}
              <button onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages} className="p-1.5 rounded-lg text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed">›</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
